import { toCamelCase } from '@huggg/bar-staff';
import { Body_4 as Body4 } from '@huggg/react-components';
import classnames from 'classnames';
import { BarCode, QRCode } from 'components/ui';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import Alert from './Alert';
import AlphaText from './AlphaText';
import BankPaymentSuccess from './BankPayment/BankPaymentSuccess';
import Fulfil from './Fulfil';
import Pin4Resend from './Pin4Resend';
import PrepaidCard from './PrepaidCard/PrepaidCard';
import Url from './Url';
import UrlTillo from './UrlTillo';
import VirginExperienceFulfil from './VirginExperienceFulfil';
import styles from './types.module.scss';

const VoucherTypes = (props) => {
  const {
    vouchers,
    redeemedSegment,
    isBlueHuggg,
    isVirginExperience,
    onFulfil,
    isGenerating,
  } = props;

  const [isWaitingForFulfil, setIsWaitingForFulfil] = useState(false);

  const handleFulfilClick = () => {
    setIsWaitingForFulfil(true);

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari) {
      // Open immediately on Safari (Safari blocks delayed popups)
      window.open(
        window.location.href.replace('/redeemed', '/reveal'),
        '_blank',
      );
      setTimeout(() => {
        setIsWaitingForFulfil(false);
      }, 1000);
    } else {
      setTimeout(() => {
        window.open(
          window.location.href.replace('/redeemed', '/reveal'),
          '_blank',
        );
        setIsWaitingForFulfil(false);
      }, 1000);
    }
  };

  const shouldShowVirginExperienceFulfil = useMemo(() => {
    return isVirginExperience && !!redeemedSegment;
  }, [isVirginExperience, redeemedSegment]);

  return (
    <>
      <div className={styles.vouchers}>
        {vouchers.map((item, index) => {
          const types = item.type.split('/');
          const typeClassName = toCamelCase('type/' + item.type);

          // If item.label has [type] in it, for blue huggg say voucher and else card
          if (item.label?.includes('[type]')) {
            item.label = item.label.replace(
              '[type]',
              isBlueHuggg ? 'voucher' : 'card',
            );
          }

          return (
            <div
              className={classnames(styles.voucherType, styles[typeClassName])}
              key={index}
            >
              {item.type !== 'url' &&
                item.type !== 'fulfil' &&
                item.type !== 'providerPaymentId' &&
                item.label && (
                  <Body4 className={styles.label}>{item.label}</Body4>
                )}

              {types[0] === 'qr' && (
                <>
                  <QRCode
                    code={item.value}
                    imageClassName={styles.qrCodeImage}
                    data-test-id="voucher-image-qr"
                  />
                </>
              )}

              {types[0] === 'barcode' && (
                <BarCode
                  code={item.value}
                  imageClassName={styles.barCodeImage}
                  data-test-id="voucher-image-barcode"
                />
              )}

              {types[0] === 'alpha' && <AlphaText data={item} />}

              {item.type === 'pin4/resend' && <Pin4Resend data={item} />}

              {types[0] === 'url' &&
                !isWaitingForFulfil &&
                item.voucherType !== 'tillo' && (
                  <Url data={item} value={item.value} />
                )}

              {types[0] === 'fulfil' &&
                (() => {
                  if (shouldShowVirginExperienceFulfil) {
                    return (
                      <VirginExperienceFulfil
                        loading={onFulfil ? isGenerating : isWaitingForFulfil}
                        onClick={onFulfil ? undefined : handleFulfilClick}
                        onFulfil={onFulfil}
                        redeemedSegment={redeemedSegment}
                      />
                    );
                  }

                  return (
                    <Fulfil
                      loading={isWaitingForFulfil}
                      onClick={handleFulfilClick}
                      isBlueHuggg={isBlueHuggg}
                    />
                  );
                })()}

              {types[0] === 'url' && item.voucherType === 'tillo' && (
                <UrlTillo
                  loading={isWaitingForFulfil}
                  vouchers={vouchers}
                  isBlueHuggg={isBlueHuggg}
                />
              )}

              {item.type === 'prepaid-card/id' && (
                <PrepaidCard id={item.value} />
              )}

              {item.type === 'providerPaymentId' && <BankPaymentSuccess />}

              {types[0] === 'alert' && (
                <Alert value={{ level: types[1], message: item.value }} />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

VoucherTypes.propTypes = {
  brandName: PropTypes.string.isRequired,
  huggg: PropTypes.object.isRequired,
  children: PropTypes.node,
  vouchers: PropTypes.array,
  redeemedSegment: PropTypes.object,
  isBlueHuggg: PropTypes.bool,
  isVirginExperience: PropTypes.bool,
  onFulfil: PropTypes.func,
  isGenerating: PropTypes.bool,
};

export default VoucherTypes;
